import { Transition } from '@headlessui/react';
import { format, parseISO } from 'date-fns';
import { HotelForm as Form } from 'hotel/HotelForm';
import { Fragment, useState } from 'react';
import { Field } from 'react-final-form';

import { Button } from 'components/Button';
import TextField from 'components/Inputs/FormInputs/TextField';
import UserBadge from 'components/UserBadge';
import { useSendRemarkResponseEmailMutation } from 'generated/graphql';
import { popToast } from 'utilities/popToast';

type Props = {
  remarks: {
    id: string;
    note: string;
    internal: boolean;
    createdAt: string;
    createdBy: {
      email: string;
      picture: string;
      username: string;
      profile?: {
        firstName?: string | null;
        lastName?: string | null;
      } | null;
    };
  }[];
};

const RemarksList = ({ remarks }: Props) => {
  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  const handleReplyClick = (id: string) => {
    setReplyingTo(replyingTo === id ? null : id);
  };

  const [sendRemarkResponseEmail] = useSendRemarkResponseEmailMutation({
    onCompleted: () => {
      popToast(`Response successfully submitted!`);
    },
    onError: (error) => {
      popToast(`Error submitting response: ${error.message}`, {
        type: 'error',
      });
    },
  });

  const handleSendClick = async (values: { replyText: string }) => {
    if (replyingTo) {
      try {
        await sendRemarkResponseEmail({
          variables: {
            input: {
              remarkId: replyingTo,
              response: values.replyText,
            },
          },
        });
      } catch (error) {
        console.error('Error sending reply:', error);
      } finally {
        setReplyingTo(null);
      }
    }
  };

  return (
    <>
      {/* Remarks list */}
      {remarks.map((remark) => (
        <div
          key={remark.id}
          className="mb-5 flex flex-col rounded-md border border-gray-200 bg-white p-5"
        >
          <div className="mb-5 size-4 w-full flex-1">{remark.note}</div>
          <div className="flex flex-row items-center justify-between">
            <div className="mr-[10px]">
              <UserBadge size="normal" user={remark.createdBy} />
            </div>
            <div className="flex size-[14px] h-auto w-full flex-row items-center justify-between text-primaryGray">
              Sent on {format(parseISO(remark.createdAt), 'M/d/yyyy')} at{' '}
              {format(parseISO(remark.createdAt), 'HH:mm')}
              <Button onClick={() => handleReplyClick(remark.id)}>
                {replyingTo === remark.id ? 'Cancel' : 'Reply'}
              </Button>
            </div>
          </div>
          <Transition
            as={Fragment}
            show={replyingTo === remark.id}
            enter="transition-all duration-300"
            enterFrom="opacity-0 max-h-0 overflow-hidden"
            enterTo="opacity-100 max-h-screen overflow-hidden"
            leave="transition-all duration-300"
            leaveFrom="opacity-100 max-h-screen overflow-hidden"
            leaveTo="opacity-0 max-h-0 overflow-hidden"
          >
            <div>
              <Form
                onSubmit={handleSendClick}
                render={({ handleSubmit }) => (
                  <form
                    onSubmit={handleSubmit}
                    className="mt-4 flex flex-col border-t pt-4"
                  >
                    <div className="flex items-center space-x-2">
                      <Field name="replyText" component={TextField} required />
                      <Button type="submit">Send</Button>
                    </div>
                  </form>
                )}
              />
            </div>
          </Transition>
        </div>
      ))}
      {/* Remarks form */}
    </>
  );
};

export default RemarksList;
